import React, { useState, useEffect, useRef } from 'react';
import audioFiles from '../../data/audioFiles.json';
import { prepareVideoUrls } from '../../utils/videoUtils';
import './Home.css';

// Composants d'icônes minimalistes style YouTube
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
  </svg>
);

const VolumeMuteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
  </svg>
);

const VolumeLowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
  </svg>
);

const VolumeHighIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
  </svg>
);

const FullscreenIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
  </svg>
);

// Importer la liste des vidéos compressées
let videoFiles = [];
try {
  const rawVideoFiles = require('../../data/videoFiles.json');
  // Encoder les URLs pour qu'elles fonctionnent en production
  videoFiles = prepareVideoUrls(rawVideoFiles);
} catch (e) {
  console.warn('Fichier videoFiles.json non trouvé. Exécutez: npm run compress-videos');
}

// Importer les données des équipements
const equipmentData = (() => {
  try {
    return require('../../data/equipmentData.json');
  } catch (e) {
    console.warn('Fichier equipmentData.json non trouvé.');
    return {};
  }
})();

// Base des fichiers audio :
// - priorite a REACT_APP_AUDIO_BASE_URL si definie
// - en local, lire depuis OTR-RADIO (port 3002)
// - en prod, fallback vers le CDN historique
const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const AUDIO_BASE_URL = (
  process.env.REACT_APP_AUDIO_BASE_URL ||
  (isLocalDev
    ? 'http://localhost:3002/OTRRADIO/assets/SOUND GALLERY'
    : 'https://wave-form.studio/assets/otr-audio')
).replace(/\/+$/, '');

// Normalise un nom de fichier pour le CDN : espaces -> tirets
const normalizeAudioFilename = (name = '') =>
  name
    .trim()
    .replace(/\s+/g, '-');

const getTrackUrl = (track) => {
  const raw = (track?.filename || track?.url || '').trim();
  if (!raw) return '';
  const normalized = normalizeAudioFilename(raw);
  return `${AUDIO_BASE_URL}/${encodeURIComponent(normalized)}`;
};

const Home = () => {
  const videoRef = useRef(null);
  const reflectionVideoRef = useRef(null);
  const [currentText, setCurrentText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  
  const [scrollY, setScrollY] = useState(0);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [showSoundCatalogPopup, setShowSoundCatalogPopup] = useState(false);
  const [showEquipmentPopup, setShowEquipmentPopup] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoStartTime] = useState(1);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRef = useRef(null);
  const audioInstancesRef = useRef(new Map()); // Stocker toutes les instances audio
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [volume, setVolume] = useState(1); // Volume entre 0 et 1
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showCategories, setShowCategories] = useState(false); // Pour afficher/masquer les catégories sur mobile
  const volumeContainerRef = useRef(null); // Référence pour le container de volume
  const previousVolumeRef = useRef(1); // Mémoriser le volume avant de couper le son
  const volumeSliderTimeoutRef = useRef(null); // Timeout pour masquer le slider automatiquement
  const [shareLinkCopied, setShareLinkCopied] = useState(false); // État pour le message de copie
  const audioContextRef = useRef(null); // AudioContext pour analyser les fichiers audio
  const trackLoadedFromUrlRef = useRef(false); // Pour éviter de recharger le track depuis l'URL
  const isLoadingTrackRef = useRef(false); // Pour éviter les chargements multiples simultanés
  const urlTrackLoadedRef = useRef(null); // Stocke l'ID du track chargé depuis l'URL pour éviter les chargements multiples
  const [pendingTrackFromUrl, setPendingTrackFromUrl] = useState(null); // Track en attente de lecture si autoplay bloqué
  const audioFromUrlRef = useRef(null); // Référence à l'audio chargé depuis l'URL
  
  // États pour la playlist vidéo
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDurationState, setVideoDurationState] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaylistExpanded, setIsPlaylistExpanded] = useState(true);
  const [videoVolume, setVideoVolume] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  const [showVideoVolumeSlider, setShowVideoVolumeSlider] = useState(false);
  const [showVideoControls, setShowVideoControls] = useState(false);
  const soundDesignVideoRef = useRef(null);
  const videoPlayerContainerRef = useRef(null);
  
  // États pour les popups explicatifs des services
  const [showMasteringPopup, setShowMasteringPopup] = useState(false);
  const [showMixagePopup, setShowMixagePopup] = useState(false);
  const [showEnregistrementPopup, setShowEnregistrementPopup] = useState(false);
  const [showProductionPopup, setShowProductionPopup] = useState(false);
  const [hoveredEquipment, setHoveredEquipment] = useState(null);
  const [equipmentTooltipPosition, setEquipmentTooltipPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [clickedEquipment, setClickedEquipment] = useState(null); // Pour mobile uniquement
  const [expandedEquipmentCategory, setExpandedEquipmentCategory] = useState(null); // Section dépliée (mobile)

  const handleOpenOtrRadio = () => {
    const url = process.env.REACT_APP_OTR_RADIO_URL || '/OTRRADIO';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      const mobile = isTouchDevice && isSmallScreen;
      setIsMobile(mobile);
      // Réinitialiser l'état si on passe de mobile à desktop
      if (!mobile) {
        setClickedEquipment(null);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fonction pour calculer la position optimale de la tooltip
  const calculateTooltipPosition = (elementRect) => {
    const tooltipWidth = 400; // Largeur approximative de la tooltip
    const tooltipHeight = 350; // Hauteur approximative de la tooltip
    const padding = 15; // Padding de sécurité
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = elementRect.right + 15; // Position par défaut à droite
    let y = elementRect.top; // Aligner avec le haut de l'élément
    
    // Ajuster si la tooltip sort à droite
    if (x + tooltipWidth > viewportWidth - padding) {
      // Essayer à gauche
      const leftX = elementRect.left - tooltipWidth - 15;
      if (leftX >= padding) {
        x = leftX;
      } else {
        // Centrer horizontalement si ni gauche ni droite ne fonctionnent
        x = Math.max(padding, (viewportWidth - tooltipWidth) / 2);
      }
    }
    
    // Ajuster si la tooltip sort en bas
    if (y + tooltipHeight > viewportHeight - padding) {
      // Remonter pour que la tooltip soit visible
      y = Math.max(padding, viewportHeight - tooltipHeight - padding);
    }
    
    // Ajuster si la tooltip sort en haut
    if (y < padding) {
      y = padding;
    }
    
    // Centrer verticalement par rapport à l'élément si possible
    const centerY = elementRect.top + (elementRect.height / 2) - (tooltipHeight / 2);
    if (centerY >= padding && centerY + tooltipHeight <= viewportHeight - padding) {
      y = centerY;
    }
    
    return { x, y };
  };

  // Effet pour précharger et optimiser la vidéo
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Force la priorité de chargement
      video.load();
      
      // Force l'accélération matérielle et optimise la lecture
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      video.playsInline = true;
      
      // Optimise les performances de rendu
      video.style.willChange = 'transform';
      video.style.transform = 'translateZ(0)';
      
      // Force la lecture automatique, surtout sur mobile
      const forceAutoPlay = async () => {
        try {
          await video.play();
          
          // Continue à forcer la lecture si elle s'arrête
          const keepPlaying = () => {
            if (video.paused) {
              video.play().catch(() => {});
            }
          };
          
          video.addEventListener('pause', keepPlaying);
          video.addEventListener('ended', () => {
            video.currentTime = videoStartTime;
            video.play().catch(() => {});
          });
          
          return () => {
            video.removeEventListener('pause', keepPlaying);
          };
        } catch (error) {
          console.log('Autoplay prevented, trying again...');
        }
      };
      
      // Optimise le buffer pour une lecture fluide
      const optimizeBuffer = () => {
        if (video.buffered.length > 0) {
          const bufferedEnd = video.buffered.end(video.buffered.length - 1);
          const bufferedStart = video.buffered.start(0);
          
          // Vitesse de lecture légèrement accélérée (1.25x)
          if (bufferedEnd - bufferedStart > 5) {
            video.playbackRate = 1.25;
          }
          if (video.readyState >= 3) {
            video.playbackRate = 1.25;
          }
        }
      };
      
      // Déclenche la lecture au premier toucher/interaction
      const handleFirstInteraction = () => {
        video.play().catch(() => {});
        document.removeEventListener('touchstart', handleFirstInteraction);
        document.removeEventListener('click', handleFirstInteraction);
      };
      
      // Essaye de jouer immédiatement
      forceAutoPlay();
      
      // Si ça échoue, attend un événement utilisateur
      if (video.paused) {
        document.addEventListener('touchstart', handleFirstInteraction, { once: true });
        document.addEventListener('click', handleFirstInteraction, { once: true });
      }
      
      video.addEventListener('progress', optimizeBuffer);
      video.addEventListener('loadeddata', optimizeBuffer);
      video.addEventListener('canplaythrough', forceAutoPlay);
      
      // Force la vidéo à continuer pendant le scroll
      const ensurePlaybackDuringScroll = () => {
        if (video && video.paused) {
          video.play().catch(() => {});
        }
      };
      
      // Écoute les événements de scroll pour maintenir la lecture
      window.addEventListener('scroll', ensurePlaybackDuringScroll, { passive: true });
      window.addEventListener('wheel', ensurePlaybackDuringScroll, { passive: true });
      window.addEventListener('touchmove', ensurePlaybackDuringScroll, { passive: true });
      
      return () => {
        video.removeEventListener('progress', optimizeBuffer);
        video.removeEventListener('loadeddata', optimizeBuffer);
        window.removeEventListener('scroll', ensurePlaybackDuringScroll);
        window.removeEventListener('wheel', ensurePlaybackDuringScroll);
        window.removeEventListener('touchmove', ensurePlaybackDuringScroll);
        document.removeEventListener('touchstart', handleFirstInteraction);
        document.removeEventListener('click', handleFirstInteraction);
      };
    }
  }, [videoStartTime]);
  
  const texts = [
    'OTR\nSTUDIO',
    'WE ARE\nMUSIC\nMAKERS'
  ];
  
  const isBold = textIndex === 0; // OTR STUDIO est en gras
  
  // Animation de frappe rapide en boucle continue
  useEffect(() => {
    const interval = setInterval(() => {
      const currentFullText = texts[textIndex];
      
      if (isTyping) {
        if (currentText.length < currentFullText.length) {
          setCurrentText(currentFullText.slice(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsTyping(false), 1000);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          setTimeout(() => {
            setTextIndex((textIndex + 1) % texts.length);
            setIsTyping(true);
          }, 500);
        }
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [currentText, isTyping, textIndex, texts]);

  // Démarrage de l'animation au chargement
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentText('W');
      setIsTyping(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Animation du curseur
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 600);
    return () => clearInterval(cursorInterval);
  }, []);

  // Effet parallax et gestion de la flèche scroll
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setIsScrolling(true);
      
      // Force la vidéo à continuer pendant le scroll
      const video = videoRef.current;
      if (video && video.paused) {
        video.play().catch(() => {});
      }
      
      // Clear timeout pour reset l'état de scroll
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
      
      // Gestion de la flèche scroll
      const heroSection = document.querySelector('.hero');
      const productionSection = document.querySelector('.production-section');
      
      if (heroSection && productionSection) {
        const heroRect = heroSection.getBoundingClientRect();
        const productionRect = productionSection.getBoundingClientRect();
        
        // La flèche est visible si :
        // 1. La section hero est encore visible (bottom > 0)
        // 2. ET on n'est pas trop proche de la section production (distance > 200px)
        const isHeroVisible = heroRect.bottom > 0;
        const distanceToProduction = productionRect.top - window.innerHeight;
        const isFarFromProduction = distanceToProduction > 200;
        
        const shouldShowArrow = isHeroVisible && isFarFromProduction;
        setShowScrollArrow(shouldShowArrow);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Formater le temps en mm:ss
  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fonction pour générer le lien de partage
  const generateShareLink = (trackId) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const shareUrl = `${baseUrl}?track=${trackId}`;
    return shareUrl;
  };

  // Fonction pour copier le lien de partage
  const copyShareLink = async (trackId) => {
    try {
      const shareUrl = generateShareLink(trackId);
      await navigator.clipboard.writeText(shareUrl);
      setShareLinkCopied(true);
      setTimeout(() => {
        setShareLinkCopied(false);
      }, 1500); // Disparaît après 1.5 secondes
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      // Fallback pour les navigateurs qui ne supportent pas clipboard API
      const shareUrl = generateShareLink(trackId);
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShareLinkCopied(true);
        setTimeout(() => {
          setShareLinkCopied(false);
        }, 1500); // Disparaît après 1.5 secondes
      } catch (fallbackErr) {
        console.error('Erreur fallback:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  // Fonction pour charger un track depuis l'URL
  const loadTrackFromUrl = (trackId, track) => {
    // Éviter les chargements multiples simultanés
    if (isLoadingTrackRef.current) {
      console.log('Chargement déjà en cours, ignoré');
      return;
    }
    
    // Vérifier si le track n'est pas déjà en cours de lecture
    if (currentlyPlaying === trackId && audioRef.current && !audioRef.current.paused) {
      console.log('Track déjà en cours de lecture');
      return; // Déjà en cours
    }
    
    console.log('Chargement du track depuis l\'URL:', trackId, track.name);
    isLoadingTrackRef.current = true;
    
    // Arrêter tous les audios en cours
    stopAllAudio();
    
    // Attendre un peu pour que les ressources soient libérées
    setTimeout(() => {
      setCurrentTime(0);
      setDuration(0);
      
      const audio = new Audio(getTrackUrl(track));
      audio.volume = volume;
      audioRef.current = audio;
      setCurrentlyPlaying(track.id);
      audioInstancesRef.current.set(track.id, audio);
      
      const handleEnded = () => {
        if (audioInstancesRef.current.has(track.id)) {
          audioInstancesRef.current.delete(track.id);
        }
        setCurrentlyPlaying(null);
        setCurrentTime(0);
        setDuration(0);
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        isLoadingTrackRef.current = false;
      };
      
      const handleLoadedMetadata = () => {
        if (audio.duration) {
          setDuration(audio.duration);
        }
      };
      
      const handleCanPlay = () => {
        console.log('Audio prêt, lancement de la lecture');
        // Lancer la lecture automatiquement
        audio.play().catch(err => {
          console.error('Erreur de lecture automatique:', err);
          // Si l'autoplay est bloqué, essayer de jouer après un court délai
          setTimeout(() => {
            audio.play().catch(secondErr => {
              console.error('Erreur de lecture après délai:', secondErr);
              audioInstancesRef.current.delete(track.id);
              setCurrentlyPlaying(null);
              if (audioRef.current === audio) {
                audioRef.current = null;
              }
              isLoadingTrackRef.current = false;
            });
          }, 500);
        });
      };
      
      const handleError = (e) => {
        console.error('Erreur de chargement audio:', e);
        isLoadingTrackRef.current = false;
      };
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('error', handleError);
      
      // Le navigateur chargera automatiquement l'audio quand on définit la source
      // Ne pas appeler load() ou preload pour éviter les conflits
    }, 300); // Délai pour libérer les ressources
  };

  // Fonction pour arrêter toutes les lectures audio
  const stopAllAudio = () => {
    console.log('stopAllAudio appelé');
    
    // Arrêter l'instance principale
    if (audioRef.current) {
      try {
        const audio = audioRef.current;
        console.log('Arrêt de audioRef.current');
        // Arrêter la lecture d'abord
        if (!audio.paused) {
          audio.pause();
        }
        audio.currentTime = 0;
        // Retirer tous les event listeners
        audio.onerror = null;
        audio.onabort = null;
        audio.onended = null;
        audio.onloadedmetadata = null;
        audio.oncanplay = null;
        audio.ontimeupdate = null;
        audio.ondurationchange = null;
        // Vider la source pour annuler le chargement en cours
        try {
          audio.removeAttribute('src');
          audio.load(); // Forcer le rechargement pour arrêter complètement
        } catch (e) {
          // Ignorer les erreurs - le navigateur peut avoir déjà annulé le chargement
        }
      } catch (err) {
        console.error('Erreur lors de l\'arrêt de audioRef:', err);
      }
      audioRef.current = null;
    }
    
    // Arrêter l'audio chargé depuis l'URL si présent
    if (audioFromUrlRef.current) {
      try {
        const audio = audioFromUrlRef.current;
        console.log('Arrêt de audioFromUrlRef.current');
        if (!audio.paused) {
          audio.pause();
        }
        audio.currentTime = 0;
        // Retirer tous les event listeners
        audio.onerror = null;
        audio.onabort = null;
        audio.onended = null;
        audio.onloadedmetadata = null;
        audio.oncanplay = null;
        audio.ontimeupdate = null;
        audio.ondurationchange = null;
        try {
          audio.removeAttribute('src');
          audio.load(); // Forcer le rechargement pour arrêter complètement
        } catch (e) {
          // Ignorer les erreurs
        }
      } catch (err) {
        console.error('Erreur lors de l\'arrêt de audioFromUrlRef:', err);
      }
      audioFromUrlRef.current = null;
    }
    
    // Nettoyer l'état du track en attente
    setPendingTrackFromUrl(null);
    
    // Arrêter toutes les autres instances
    audioInstancesRef.current.forEach((audio, trackId) => {
      if (audio) {
        try {
          console.log('Arrêt de l\'instance audio pour trackId:', trackId);
          if (!audio.paused) {
            audio.pause();
          }
          audio.currentTime = 0;
          // Retirer tous les event listeners
          audio.onerror = null;
          audio.onabort = null;
          audio.onended = null;
          audio.onloadedmetadata = null;
          audio.oncanplay = null;
          audio.ontimeupdate = null;
          audio.ondurationchange = null;
          // Vider la source pour annuler le chargement en cours
          try {
            audio.removeAttribute('src');
            audio.load(); // Forcer le rechargement pour arrêter complètement
          } catch (e) {
            // Ignorer les erreurs
          }
        } catch (err) {
          console.error('Erreur lors de l\'arrêt de l\'instance audio:', err);
        }
      }
    });
    audioInstancesRef.current.clear();
    setCurrentlyPlaying(null);
    setCurrentTime(0);
    setDuration(0);
    console.log('stopAllAudio terminé');
  };

  // Appliquer le volume à l'audio
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
    // Appliquer aussi aux autres instances
    audioInstancesRef.current.forEach((audio) => {
      if (audio) {
        audio.volume = volume;
      }
    });
  }, [volume]);

  // Mettre à jour le temps de lecture
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentlyPlaying) {
      setCurrentTime(0);
      setDuration(0);
      return;
    }

    const updateTime = () => {
      if (!isSeeking && audio && currentlyPlaying) {
        setCurrentTime(audio.currentTime);
      }
    };

    const updateDuration = () => {
      if (audio && currentlyPlaying) {
        setDuration(audio.duration || 0);
      }
    };

    // S'assurer que la durée est mise à jour si elle est déjà disponible
    if (audio.duration) {
      setDuration(audio.duration);
    }

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
    };
  }, [currentlyPlaying, isSeeking]); // Utiliser currentlyPlaying comme dépendance principale

  // Gérer les événements fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      setIsFullscreen(isFullscreenNow);
      // Toujours afficher les contrôles en fullscreen
      if (isFullscreenNow) {
        setShowVideoControls(true);
      } else {
        setShowVideoControls(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Appliquer le volume et le mute à la vidéo
  useEffect(() => {
    if (soundDesignVideoRef.current) {
      const volume = Math.max(0, Math.min(1, videoVolume));
      soundDesignVideoRef.current.volume = volume;
      soundDesignVideoRef.current.muted = volume === 0 || videoMuted;
    }
  }, [videoVolume, videoMuted, currentVideoIndex]);

  // Nettoyer l'audio quand le popup se ferme
  useEffect(() => {
    if (!showSoundCatalogPopup) {
      stopAllAudio();
      setSearchQuery('');
      setSelectedCategory('all');
      setCurrentTime(0);
      setDuration(0);
      // Réinitialiser les refs quand le popup se ferme pour permettre de recharger depuis l'URL
      trackLoadedFromUrlRef.current = false;
      isLoadingTrackRef.current = false;
      urlTrackLoadedRef.current = null; // Réinitialiser pour permettre de recharger un nouveau track
    }
  }, [showSoundCatalogPopup]);

  // Vérifier si on revient d'un envoi réussi de formulaire
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      alert('✅ Message envoyé avec succès ! L\'équipe OTR STUDIO vous répondra rapidement.');
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setShowContactPopup(false);
    }
  }, []);

  // Vérifier l'URL au montage initial et ouvrir le popup si nécessaire
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trackIdParam = urlParams.get('track');
    
    if (trackIdParam && audioFiles.length > 0) {
      const trackId = parseInt(trackIdParam, 10);
      const track = audioFiles.find(t => t.id === trackId);
      
      if (track) {
        console.log('Track trouvé dans l\'URL au montage:', trackId, track.name);
        // Réinitialiser le ref pour permettre le chargement
        urlTrackLoadedRef.current = null;
        // Ouvrir le popup automatiquement
        setShowSoundCatalogPopup(true);
      }
    }
  }, []); // Se déclenche uniquement au montage initial

  // Charger automatiquement un track depuis l'URL quand le popup s'ouvre
  useEffect(() => {
    if (!showSoundCatalogPopup) {
      // Réinitialiser le ref quand le popup se ferme
      urlTrackLoadedRef.current = false;
      return; // Ne rien faire si le popup n'est pas ouvert
    }

    const urlParams = new URLSearchParams(window.location.search);
    const trackIdParam = urlParams.get('track');
    
    console.log('useEffect URL track - popup ouvert:', showSoundCatalogPopup, 'trackIdParam:', trackIdParam, 'urlTrackLoadedRef:', urlTrackLoadedRef.current);
    
    if (!trackIdParam) {
      console.log('Pas de track dans l\'URL');
      return; // Pas de track dans l'URL
    }
    
    // Vérifier si on a déjà chargé ce track spécifique
    const currentTrackId = parseInt(trackIdParam, 10);
    if (urlTrackLoadedRef.current === currentTrackId) {
      console.log('Track déjà chargé depuis l\'URL:', currentTrackId);
      return; // Déjà chargé
    }
    
    if (audioFiles.length > 0) {
      const track = audioFiles.find(t => t.id === currentTrackId);
      
      if (track) {
        console.log('Popup ouvert, chargement du track:', currentTrackId, track.name);
        
        // Marquer comme chargé avec l'ID du track pour éviter les chargements multiples
        urlTrackLoadedRef.current = currentTrackId;
        
        // Éviter les chargements multiples simultanés
        if (isLoadingTrackRef.current) {
          console.log('Chargement déjà en cours, ignoré');
          return;
        }
        
        // Vérifier si le track n'est pas déjà en cours de lecture
        if (currentlyPlaying === currentTrackId && audioRef.current && !audioRef.current.paused) {
          console.log('Track déjà en cours de lecture');
          return;
        }
        
        // Attendre que le popup soit complètement rendu
        const timeoutId = setTimeout(() => {
          console.log('Début du chargement du track après délai');
          isLoadingTrackRef.current = true;
          
          // Arrêter tous les audios en cours
          stopAllAudio();
          
          // Attendre un peu pour que les ressources soient libérées
          setTimeout(() => {
            console.log('Création de l\'instance audio pour:', track.name);
            setCurrentTime(0);
            setDuration(0);
            
            const audio = new Audio(getTrackUrl(track));
            audio.volume = volume;
            audioRef.current = audio;
            setCurrentlyPlaying(track.id);
            audioInstancesRef.current.set(track.id, audio);
            
            const handleEnded = () => {
              if (audioInstancesRef.current.has(track.id)) {
                audioInstancesRef.current.delete(track.id);
              }
              setCurrentlyPlaying(null);
              setCurrentTime(0);
              setDuration(0);
              if (audioRef.current === audio) {
                audioRef.current = null;
              }
              isLoadingTrackRef.current = false;
            };
            
            const handleLoadedMetadata = () => {
              console.log('Métadonnées chargées, durée:', audio.duration);
              if (audio.duration) {
                setDuration(audio.duration);
              }
            };
            
            const handleCanPlay = () => {
              console.log('Audio prêt, lancement de la lecture pour:', track.name);
              // Stocker l'audio dans le ref pour pouvoir le lancer plus tard si autoplay bloqué
              audioFromUrlRef.current = audio;
              
              // Lancer la lecture automatiquement
              audio.play().then(() => {
                console.log('Lecture démarrée avec succès');
                audioFromUrlRef.current = null; // Nettoyer le ref si ça fonctionne
                setPendingTrackFromUrl(null);
              }).catch(err => {
                console.error('Erreur de lecture automatique (autoplay probablement bloqué):', err);
                // Si l'autoplay est bloqué, stocker le track pour le lancer au prochain clic
                setPendingTrackFromUrl(track);
                isLoadingTrackRef.current = false;
              });
            };
            
            const handleError = (e) => {
              console.error('Erreur de chargement audio:', e, track.url);
              isLoadingTrackRef.current = false;
            };
            
            audio.addEventListener('ended', handleEnded);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('canplay', handleCanPlay);
            audio.addEventListener('error', handleError);
            
            console.log('Event listeners ajoutés, chargement en cours...');
          }, 300);
        }, 1000); // Augmenter le délai pour s'assurer que le popup est complètement rendu
        
        return () => clearTimeout(timeoutId);
      } else {
        console.warn('Track non trouvé avec l\'ID:', currentTrackId, 'audioFiles disponibles:', audioFiles.length);
      }
    } else {
      console.warn('audioFiles pas encore chargé, longueur:', audioFiles.length);
    }
  }, [showSoundCatalogPopup, currentlyPlaying, volume]); // Se déclenche quand le popup s'ouvre

  // Extraire les catégories des fichiers audio
  const extractCategories = () => {
    const categoriesMap = new Map(); // Map pour regrouper les variantes
    const categoryKeywords = [
      { variants: ['HIP HOP', 'HIPHOP'], display: 'HIP HOP' },
      { variants: ['TRIP HOP', 'TRIPHOP'], display: 'TRIP HOP' },
      { variants: ['DRUM N BASS', 'DNB', 'DRUM & BASS'], display: 'DRUM N BASS' },
      { variants: ['JAZZ'], display: 'JAZZ' },
      { variants: ['AMBIENT'], display: 'AMBIENT' },
      { variants: ['ROCK'], display: 'ROCK' },
      { variants: ['BOOMBAP'], display: 'BOOMBAP' },
      { variants: ['LOFI'], display: 'LOFI' },
      { variants: ['ELECTRONIC'], display: 'ELECTRONIC' },
      { variants: ['SYNTH'], display: 'SYNTH' },
      { variants: ['FUNK'], display: 'FUNK' },
      { variants: ['SOUL'], display: 'SOUL' },
      { variants: ['TRAP'], display: 'TRAP' },
      { variants: ['GLITCH'], display: 'GLITCH' },
      { variants: ['EXPERIMENTAL'], display: 'EXPERIMENTAL' },
      { variants: ['POP'], display: 'POP' },
      { variants: ['HOUSE'], display: 'HOUSE' },
      { variants: ['ACID'], display: 'ACID' },
      { variants: ['BREAKBEAT'], display: 'BREAKBEAT' },
      { variants: ['ATMOS'], display: 'ATMOS' },
      { variants: ['CYBER'], display: 'CYBER' },
      { variants: ['FUTURE'], display: 'FUTURE' },
      { variants: ['ABSTRACT'], display: 'ABSTRACT' },
      { variants: ['NOISE'], display: 'NOISE' },
      { variants: ['GRANULAR'], display: 'GRANULAR' },
      { variants: ['VOCODER'], display: 'VOCODER' },
      { variants: ['STEP'], display: 'STEP' },
      { variants: ['BEAT'], display: 'BEAT' }
    ];

    audioFiles.forEach(track => {
      const name = track.name.toUpperCase();
      categoryKeywords.forEach(({ variants, display }) => {
        // Vérifier si le nom contient une des variantes
        const found = variants.some(variant => name.includes(variant));
        if (found) {
          categoriesMap.set(display, true);
        }
      });
    });

    return Array.from(categoriesMap.keys()).sort();
  };

  // Extraire les catégories et filtrer les fichiers audio
  const categories = React.useMemo(() => extractCategories(), []);
  
  // Map des variantes pour chaque catégorie
  const categoryVariantsMap = React.useMemo(() => {
    const map = new Map();
    const categoryKeywords = [
      { variants: ['HIP HOP', 'HIPHOP'], display: 'HIP HOP' },
      { variants: ['TRIP HOP', 'TRIPHOP'], display: 'TRIP HOP' },
      { variants: ['DRUM N BASS', 'DNB', 'DRUM & BASS'], display: 'DRUM N BASS' },
      { variants: ['JAZZ'], display: 'JAZZ' },
      { variants: ['AMBIENT'], display: 'AMBIENT' },
      { variants: ['ROCK'], display: 'ROCK' },
      { variants: ['BOOMBAP'], display: 'BOOMBAP' },
      { variants: ['LOFI'], display: 'LOFI' },
      { variants: ['ELECTRONIC'], display: 'ELECTRONIC' },
      { variants: ['SYNTH'], display: 'SYNTH' },
      { variants: ['FUNK'], display: 'FUNK' },
      { variants: ['SOUL'], display: 'SOUL' },
      { variants: ['TRAP'], display: 'TRAP' },
      { variants: ['GLITCH'], display: 'GLITCH' },
      { variants: ['EXPERIMENTAL'], display: 'EXPERIMENTAL' },
      { variants: ['POP'], display: 'POP' },
      { variants: ['HOUSE'], display: 'HOUSE' },
      { variants: ['ACID'], display: 'ACID' },
      { variants: ['BREAKBEAT'], display: 'BREAKBEAT' },
      { variants: ['ATMOS'], display: 'ATMOS' },
      { variants: ['CYBER'], display: 'CYBER' },
      { variants: ['FUTURE'], display: 'FUTURE' },
      { variants: ['ABSTRACT'], display: 'ABSTRACT' },
      { variants: ['NOISE'], display: 'NOISE' },
      { variants: ['GRANULAR'], display: 'GRANULAR' },
      { variants: ['VOCODER'], display: 'VOCODER' },
      { variants: ['STEP'], display: 'STEP' },
      { variants: ['BEAT'], display: 'BEAT' }
    ];
    
    categoryKeywords.forEach(({ variants, display }) => {
      map.set(display, variants);
    });
    
    return map;
  }, []);
  
  const filteredAudioFiles = React.useMemo(() => {
    return audioFiles.filter(track => {
      const matchesSearch = track.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (selectedCategory === 'all') {
        return matchesSearch;
      }
      
      // Vérifier si le morceau correspond à une des variantes de la catégorie sélectionnée
      const variants = categoryVariantsMap.get(selectedCategory) || [selectedCategory];
      const trackNameUpper = track.name.toUpperCase();
      const matchesCategory = variants.some(variant => trackNameUpper.includes(variant));
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, categoryVariantsMap]);

  return (
    <div className={`home ${isScrolling ? 'scrolling' : ''}`}>
      <div className="site-bg-video-wrap" aria-hidden="true">
        <video
          className="site-bg-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src="/assets/videos/stud-wars.mp4" type="video/mp4" />
        </video>
      </div>
      <section id="home" className="hero">
            <div className="hero-video-wrapper">
            <video 
              ref={videoRef}
              key="hero-video"
              className="hero-video" 
              autoPlay 
              muted 
              loop 
              playsInline
              webkit-playsinline="true"
              preload="auto"
              crossOrigin="anonymous"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              style={{
                transform: `translate3d(0, ${scrollY * 0.4}px, 0)`,
                pointerEvents: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
              }}
              currentTime={videoStartTime}
              onLoadedMetadata={(e) => {
                setVideoDuration(e.target.duration);
                e.target.currentTime = videoStartTime;
                e.target.playbackRate = 1.25;
                e.target.play().catch(() => {});
              }}
              onLoadedData={(e) => {
                const video = e.target;
                if (video.buffered.length > 0) {
                  const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                  if (bufferedEnd === video.duration) {
                    video.playbackRate = 1.25;
                  }
                }
              }}
              onProgress={(e) => {
                const video = e.target;
                if (video.buffered.length > 0) {
                  const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                  if (bufferedEnd > video.duration * 0.5) {
                    video.playbackRate = 1.25;
                  }
                }
              }}
              onTimeUpdate={(e) => {
                const video = e.target;
                if (videoDuration > 0) {
                  if (video.currentTime >= videoDuration - 0.1) {
                    video.currentTime = videoStartTime;
                  }
                }
              }}
              onSeeking={(e) => {
                const video = e.target;
                video.playbackRate = 1.25;
              }}
              onCanPlay={() => {
                const video = videoRef.current;
                if (video) {
                  video.playbackRate = 1.25;
                  video.play().catch(() => {});
                }
              }}
              onPlay={() => {
                const video = videoRef.current;
                if (video) {
                  video.playbackRate = 1.25;
                }
              }}
            >
              <source src="/assets/videos/stud-wars.mp4" type="video/mp4" />
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>
            </div>
            <div className="hero-logo-otr">
              <img src="/assets/icons/logo-otr-studio.png" alt="O.T.R. STUDIO" />
            </div>
      <div className="hero-radio-cta">
        <button
          type="button"
          className="hero-radio-button"
          onClick={handleOpenOtrRadio}
        >
          <span className="hero-radio-dot" aria-hidden="true" />
          <span className="hero-radio-label">
            LANCER{' '}
            <span className="hero-radio-title-wave" aria-hidden="true">
              {'OTR RADIO'.split('').map((char, i) => (
                <span
                  key={i}
                  className="hero-radio-title-wave-char"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </span>
          </span>
        </button>
      </div>
        
        {/* Flèche de scroll pixelée */}
        {showScrollArrow && (
          <div className="scroll-indicator">
            <img 
              src="/assets/icons/menu-deroulant.png" 
              alt="" 
              className="scroll-arrow-img"
            />
          </div>
        )}
      </section>
      
      <section id="production" className="production-section">
        <div className="container production-container">
          <div className="page-section-title">
            <h2 className="production-title">PRODUCTION</h2>
          </div>
          <div className="production-columns">
            <div className="production-column">
              <h3>MIX/MASTER</h3>
              <div className="spotify-playlist">
                <iframe 
                  data-testid="embed-iframe"
                  style={{borderRadius: '12px'}}
                  src="https://open.spotify.com/embed/playlist/6KRtdsSils8MFKmTGradLb?utm_source=generator&theme=0"
                  width="100%" 
                  height="352" 
                  frameBorder="0" 
                  allowfullscreen="" 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy">
                </iframe>
              </div>
            </div>

            <div className="production-column">
              <h3>REALISATION</h3>
              <div className="spotify-playlist">
                <iframe 
                  data-testid="embed-iframe"
                  style={{borderRadius: '12px'}}
                  src="https://open.spotify.com/embed/playlist/7sWAqNU9EfadUzZtbHtvr9?utm_source=generator&theme=0"
                  width="100%" 
                  height="352" 
                  frameBorder="0" 
                  allowfullscreen="" 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy">
                </iframe>
              </div>
            </div>

            <div className="production-column sound-design-column-home">
              <h3>SYNCHRO/SOUND DESIGN</h3>
              
              <div className="video-container-home">
                {videoFiles.length > 0 && videoFiles[currentVideoIndex] && (
                  <div 
                    ref={videoPlayerContainerRef}
                    className="video-player-main-home"
                    onMouseEnter={() => {
                      setShowVideoControls(true);
                    }}
                    onMouseLeave={() => {
                      setShowVideoControls(false);
                    }}
                    onMouseMove={() => {
                      setShowVideoControls(true);
                    }}
                  >
                    <video
                      ref={soundDesignVideoRef}
                      src={videoFiles[currentVideoIndex]?.url || ''}
                      className="main-video-home"
                      preload="metadata"
                      playsInline
                      crossOrigin="anonymous"
                      onPlay={() => {
                        setIsVideoPlaying(true);
                        // S'assurer que le son est activé
                        if (soundDesignVideoRef.current) {
                          soundDesignVideoRef.current.muted = false;
                          soundDesignVideoRef.current.volume = videoVolume;
                        }
                      }}
                      onPause={() => setIsVideoPlaying(false)}
                      onError={(e) => {
                        const video = e.target;
                        const currentVideo = videoFiles[currentVideoIndex];
                        console.error('❌ Erreur de chargement de la vidéo:', {
                          videoName: currentVideo?.name,
                          url: currentVideo?.url,
                          src: video.src,
                          errorCode: video.error?.code,
                          errorMessage: video.error?.message,
                          networkState: video.networkState,
                          readyState: video.readyState
                        });
                        
                        if (video.error) {
                          // Code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
                          if (video.error.code === 4) {
                            console.error('⚠️ Format non supporté ou fichier introuvable. Vérifiez que le fichier existe sur le serveur.');
                            console.error('📍 URL attendue:', currentVideo?.url);
                            console.error('📍 URL réelle dans la balise video:', video.src);
                          }
                          // Code 2 = MEDIA_ERR_NETWORK
                          if (video.error.code === 2) {
                            console.error('⚠️ Erreur réseau. Vérifiez la connexion et que le fichier est accessible.');
                          }
                          // Code 3 = MEDIA_ERR_DECODE
                          if (video.error.code === 3) {
                            console.error('⚠️ Erreur de décodage. Le fichier vidéo pourrait être corrompu.');
                          }
                          
                          // Passer à la vidéo suivante si possible
                          if (currentVideoIndex < videoFiles.length - 1) {
                            console.log('⏭️ Tentative de chargement de la vidéo suivante...');
                            setTimeout(() => {
                              setCurrentVideoIndex(currentVideoIndex + 1);
                            }, 500);
                          } else if (currentVideoIndex > 0) {
                            // Essayer la vidéo précédente
                            console.log('⏮️ Tentative de chargement de la vidéo précédente...');
                            setTimeout(() => {
                              setCurrentVideoIndex(0);
                            }, 500);
                          }
                        }
                      }}
                      onEnded={() => {
                        setIsVideoPlaying(false);
                        // Passer à la vidéo suivante
                        if (currentVideoIndex < videoFiles.length - 1) {
                          setCurrentVideoIndex(currentVideoIndex + 1);
                        }
                      }}
                      onTimeUpdate={() => {
                        if (soundDesignVideoRef.current) {
                          setVideoCurrentTime(soundDesignVideoRef.current.currentTime);
                        }
                      }}
                      onLoadedMetadata={() => {
                        if (soundDesignVideoRef.current) {
                          setVideoDurationState(soundDesignVideoRef.current.duration || 0);
                          // S'assurer que la vidéo reste en pause après le chargement
                          soundDesignVideoRef.current.pause();
                          // S'assurer que le son est activé et le volume défini
                          soundDesignVideoRef.current.muted = false;
                          soundDesignVideoRef.current.volume = videoVolume || 1;
                          // Empêcher l'autoplay
                          soundDesignVideoRef.current.currentTime = 0;
                        }
                      }}
                      onCanPlay={() => {
                        // S'assurer que le son est activé quand la vidéo peut être lue
                        if (soundDesignVideoRef.current) {
                          soundDesignVideoRef.current.muted = false;
                          soundDesignVideoRef.current.volume = videoVolume || 1;
                          // S'assurer que la vidéo reste en pause
                          if (!isVideoPlaying) {
                            soundDesignVideoRef.current.pause();
                          }
                        }
                      }}
                    />
                    <div className={`video-controls-overlay-home ${showVideoControls ? 'visible' : ''}`}>
                      <div className="video-progress-bar-overlay-home" 
                        onClick={(e) => {
                          if (soundDesignVideoRef.current && videoDurationState > 0) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickX = e.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                            const newTime = percentage * videoDurationState;
                            soundDesignVideoRef.current.currentTime = newTime;
                            setVideoCurrentTime(newTime);
                          }
                        }}
                      >
                        <div className="video-progress-fill-overlay-home"
                          style={{ width: `${videoDurationState > 0 ? (videoCurrentTime / videoDurationState) * 100 : 0}%` }}
                        />
                        <div 
                          className="video-progress-scrubber-home"
                          style={{ left: `${videoDurationState > 0 ? (videoCurrentTime / videoDurationState) * 100 : 0}%` }}
                        />
              </div>
            </div>
                    <div className={`video-controls-main-home ${showVideoControls ? 'visible' : ''}`}>
                      <button 
                        className="play-pause-btn-home" 
                        onClick={() => {
                          if (soundDesignVideoRef.current) {
                            if (isVideoPlaying) {
                              soundDesignVideoRef.current.pause();
                            } else {
                              soundDesignVideoRef.current.play();
                            }
                            setIsVideoPlaying(!isVideoPlaying);
                          }
                        }}
                      >
                        {isVideoPlaying ? <PauseIcon /> : <PlayIcon />}
                      </button>
                      <div className="video-time-home">
                        <span>{formatTime(videoCurrentTime)}</span>
                        <span> / </span>
                        <span>{formatTime(videoDurationState)}</span>
                      </div>
                      <div className="video-controls-right-home">
                        <div className="video-volume-control-home">
                          <button 
                            className="volume-btn-home" 
                            onDoubleClick={() => {
                              setVideoMuted(!videoMuted);
                              if (soundDesignVideoRef.current) {
                                soundDesignVideoRef.current.muted = !videoMuted;
                              }
                            }}
                            onMouseEnter={() => {
                              setShowVideoVolumeSlider(true);
                              // Masquer après 3 secondes d'inactivité
                              if (window.volumeSliderTimeout) {
                                clearTimeout(window.volumeSliderTimeout);
                              }
                              window.volumeSliderTimeout = setTimeout(() => {
                                setShowVideoVolumeSlider(false);
                              }, 3000);
                            }}
                            onMouseLeave={() => {
                              // Masquer le slider après un court délai
                              if (window.volumeSliderTimeout) {
                                clearTimeout(window.volumeSliderTimeout);
                              }
                              window.volumeSliderTimeout = setTimeout(() => {
                                setShowVideoVolumeSlider(false);
                              }, 500);
                            }}
                            title="Volume (double-clic pour couper/activer)"
                          >
                            {videoMuted || videoVolume === 0 ? <VolumeMuteIcon /> : videoVolume < 0.5 ? <VolumeLowIcon /> : <VolumeHighIcon />}
                          </button>
                          {showVideoVolumeSlider && (
                            <div 
                              className="volume-slider-container-home"
                              onMouseEnter={() => {
                                // Annuler le timeout si on survole le slider
                                if (window.volumeSliderTimeout) {
                                  clearTimeout(window.volumeSliderTimeout);
                                }
                                // Masquer après 3 secondes d'inactivité
                                window.volumeSliderTimeout = setTimeout(() => {
                                  setShowVideoVolumeSlider(false);
                                }, 3000);
                              }}
                              onMouseLeave={() => {
                                setShowVideoVolumeSlider(false);
                                if (window.volumeSliderTimeout) {
                                  clearTimeout(window.volumeSliderTimeout);
                                }
                              }}
                              onMouseMove={() => {
                                // Réinitialiser le timeout à chaque mouvement
                                if (window.volumeSliderTimeout) {
                                  clearTimeout(window.volumeSliderTimeout);
                                }
                                window.volumeSliderTimeout = setTimeout(() => {
                                  setShowVideoVolumeSlider(false);
                                }, 3000);
                              }}
                            >
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={videoVolume}
                                onChange={(e) => {
                                  const newVolume = Math.max(0, Math.min(1, parseFloat(e.target.value)));
                                  setVideoVolume(newVolume);
                                  if (soundDesignVideoRef.current) {
                                    soundDesignVideoRef.current.volume = newVolume;
                                    soundDesignVideoRef.current.muted = newVolume === 0;
                                    setVideoMuted(newVolume === 0);
                                  }
                                }}
                                onInput={(e) => {
                                  const newVolume = Math.max(0, Math.min(1, parseFloat(e.target.value)));
                                  setVideoVolume(newVolume);
                                  if (soundDesignVideoRef.current) {
                                    soundDesignVideoRef.current.volume = newVolume;
                                    soundDesignVideoRef.current.muted = newVolume === 0;
                                    setVideoMuted(newVolume === 0);
                                  }
                                }}
                                className="volume-slider-home"
                                orient="vertical"
                              />
                            </div>
                          )}
                        </div>
                        <button 
                          className="fullscreen-btn-home" 
                          onClick={async () => {
                            try {
                              if (!document.fullscreenElement) {
                                if (videoPlayerContainerRef.current) {
                                  await videoPlayerContainerRef.current.requestFullscreen();
                                }
                              } else {
                                await document.exitFullscreen();
                              }
                            } catch (err) {
                              console.error('Erreur fullscreen:', err);
                            }
                          }}
                          title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                        >
                          <FullscreenIcon />
                        </button>
                      </div>
                    </div>
                    <div className="video-title-main-home">
                      {videoFiles[currentVideoIndex]?.name || ''}
                    </div>
                  </div>
                )}

                {/* Playlist normale (hors fullscreen) */}
                {!isFullscreen && (
                  <div className={`video-playlist-home ${!isPlaylistExpanded ? 'collapsed' : ''}`}>
                  {isPlaylistExpanded && (
                    <div className="playlist-items-home">
                    {videoFiles.length > 0 ? (
                      videoFiles.map((video, index) => (
                        <div
                          key={video.id}
                          className={`playlist-item-home ${index === currentVideoIndex ? 'active' : ''}`}
                          onClick={() => {
                            setCurrentVideoIndex(index);
                            setIsVideoPlaying(false);
                            if (soundDesignVideoRef.current) {
                              soundDesignVideoRef.current.load();
                            }
                          }}
                        >
                          <div className="playlist-item-number-home">{index + 1}</div>
                          <div className="playlist-item-name-home">{video.name}</div>
                          {index === currentVideoIndex && isVideoPlaying && (
                            <div className="playlist-item-playing-home">
                              <PlayIcon />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="no-videos-home">
                        <p>Aucune vidéo disponible.</p>
                        <p className="hint">Exécutez: npm run compress-videos</p>
                      </div>
                    )}
                    </div>
                  )}
                  <div 
                    className="playlist-header-home"
                    onClick={() => setIsPlaylistExpanded(!isPlaylistExpanded)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>Playlist ({videoFiles.length} vidéos)</span>
                    <span className="playlist-toggle-icon">{isPlaylistExpanded ? '▼' : '▲'}</span>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>

          <div className="production-catalog-wrap">
            <button
              className="action-btn sound-catalog-btn"
              type="button"
              onClick={() => setShowSoundCatalogPopup(true)}
            >
              SOUND CATALOG {'>'}
                </button>
                <button
                  className="action-btn sound-catalog-btn"
                  type="button"
                  onClick={handleOpenOtrRadio}
                >
                  WEB RADIO {'>'}
          </button>
          </div>
        </div>
      </section>

      <section id="services" className="services-section">
        <div className="container">
          <div className="page-section-title services-header">
            <h2 className="services-title">SERVICES</h2>
          </div>
              <div className="services-grid">
                <div className="service-card">
                  <div className="service-icon">
                    <img src="/assets/images/mastering icone_.png" alt="Mastering" />
                  </div>
                  <div className="service-body">
                    <h3 className="service-title-with-icon">
                      <button 
                        className="service-info-icon" 
                        onClick={() => setShowMasteringPopup(true)}
                        title="En savoir plus"
                        aria-label="En savoir plus sur le mastering"
                      >
                        ?
                      </button>
                      MASTERING
                    </h3>
                    <div className="service-content">
                    <p>Finalisation professionnelle de tes morceaux pour un rendu optimal sur tous les systèmes. Optimisation EQ, compression et harmonie stéréo pour un rendu commercial de qualité.</p>
                    <div className="service-price"><span className="service-price-amount">50€</span> HT/morceau</div>
                    </div>
                  </div>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">
                    <img src="/assets/images/mixxage icone.png" alt="Mixage" />
                  </div>
                  <div className="service-body">
                    <h3 className="service-title-with-icon">
                      <button 
                        className="service-info-icon" 
                        onClick={() => setShowMixagePopup(true)}
                        title="En savoir plus"
                        aria-label="En savoir plus sur le mixage"
                      >
                        ?
                      </button>
                      MIXAGE
                    </h3>
                    <div className="service-content">
                    <p>Arrangement sonore et équilibrage de tes pistes pour créer une cohérence musicale. Placement spatial, réglage des niveaux et traitement des éléments sonores pour un mix professionnel.</p>
                    <div className="service-price"><span className="service-price-amount">100€</span> HT/morceau</div>
                    </div>
                  </div>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">
                    <img src="/assets/images/renregistremet icone.png" alt="Enregistrement" />
                  </div>
                  <div className="service-body">
                    <h3 className="service-title-with-icon">
                      <button 
                        className="service-info-icon" 
                        onClick={() => setShowEnregistrementPopup(true)}
                        title="En savoir plus"
                        aria-label="En savoir plus sur l'enregistrement"
                      >
                        ?
                      </button>
                      ENREGISTREMENT
                    </h3>
                    <div className="service-content">
                    <p>Captation de voix, instruments et sources sonores dans un environnement acoustique optimal. Mise à disposition du matériel professionnel et expertise technique pour des prises de qualité.</p>
                    <div className="service-price"><span className="service-price-amount">40€</span> HT/heure</div>
                    </div>
                  </div>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">
                    <img src="/assets/images/productioicone.png" alt="Production" />
                  </div>
                  <div className="service-body">
                    <h3 className="service-title-with-icon">
                      <button 
                        className="service-info-icon" 
                        onClick={() => setShowProductionPopup(true)}
                        title="En savoir plus"
                        aria-label="En savoir plus sur la production"
                      >
                        ?
                      </button>
                      PRODUCTION
                    </h3>
                    <div className="service-content">
                    <p>Accompagnement complet de ton projet musical de l'idée initiale à la réalisation finale. Création, composition, arrangement et conseil artistique pour donner vie à ta vision musicale.</p>
                    <div className="service-price"><span className="service-price-amount">SUR DEVIS</span></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="services-equipment-wrap">
                <button className="equipment-btn" onClick={() => setShowEquipmentPopup(true)}>MATERIEL DISPONIBLE {'>'}</button>
              </div>
        </div>
        
        {/* Popups explicatifs des services */}
        {showMasteringPopup && (
          <div className="service-popup-overlay" onClick={() => setShowMasteringPopup(false)}>
            <div className="service-popup" onClick={(e) => e.stopPropagation()}>
              <button className="service-popup-close" onClick={() => setShowMasteringPopup(false)}>×</button>
              <h3>MASTERING</h3>
              <p>Le mastering est l'étape finale de post-production audio qui consiste à finaliser et optimiser un morceau pour sa diffusion commerciale. Cette étape permet d'harmoniser le rendu sonore sur tous les systèmes d'écoute et de garantir que votre musique sonne professionnelle et compétitive sur toutes les plateformes de streaming.</p>
              <p>Nos techniques incluent :</p>
              <ul>
                <li><strong>Optimisation de l'égalisation (EQ)</strong> : Analyse fine du spectre fréquentiel avec notre SSL FUSION et plugins Fabfilter Pro Suite pour équilibrer les fréquences, corriger les déséquilibres et créer une cohérence sonore. Utilisation d'égaliseurs paramétriques pour des ajustements précis des basses, médiums et aigus.</li>
                <li><strong>Compression et limitation</strong> : Gestion de la dynamique avec compresseurs 1176 KT et suite UAD pour obtenir un niveau optimal et compétitif tout en préservant la musicalité. Utilisation de compresseurs multibandes et de limiteurs pour contrôler les pics et maximiser le volume sans distorsion.</li>
                <li><strong>Harmonisation stéréo et spatialisation</strong> : Vérification et amélioration de l'image stéréo avec interface APOLLO X16, correction de la largeur et de la profondeur du champ sonore pour une écoute équilibrée.</li>
                <li><strong>Correction des problèmes de phase</strong> : Détection et correction des problèmes de phase entre les canaux pour éviter les annulations de fréquences et garantir une cohérence stéréo optimale.</li>
                <li><strong>Préparation pour différents formats</strong> : Export optimisé pour streaming (Spotify, Apple Music, etc.), CD, vinyle ou autres supports, avec respect des spécifications techniques de chaque plateforme. Possibilité de mastering analogique via Revox A77.</li>
              </ul>
              <p>Le mastering transforme votre mix final en un produit prêt pour la diffusion, avec un son professionnel qui sonne parfaitement sur tous les systèmes d'écoute.</p>
            </div>
          </div>
        )}
        
        {showMixagePopup && (
          <div className="service-popup-overlay" onClick={() => setShowMixagePopup(false)}>
            <div className="service-popup" onClick={(e) => e.stopPropagation()}>
              <button className="service-popup-close" onClick={() => setShowMixagePopup(false)}>×</button>
              <h3>MIXAGE</h3>
              <p>Le mixage consiste à équilibrer et arranger toutes les pistes d'un morceau pour créer une cohérence musicale et une expérience d'écoute optimale. C'est à cette étape que chaque élément trouve sa place dans l'espace stéréo et que les fréquences se complètent harmonieusement.</p>
              <p>Notre approche comprend :</p>
              <ul>
                <li><strong>Équilibrage des niveaux</strong> : Ajustement précis du volume de chaque piste via interface APOLLO X16 pour créer une hiérarchie sonore claire. Mise en avant des éléments importants (voix, mélodie principale) tout en préservant l'équilibre global du morceau.</li>
                <li><strong>Placement spatial (panoramique)</strong> : Utilisation du panoramique avec Ableton Live 12 SUITE pour créer de la profondeur et de la largeur dans le mix. Positionnement des éléments dans l'espace stéréo pour éviter les conflits fréquentiels et créer une image sonore cohérente.</li>
                <li><strong>Traitement des éléments sonores</strong> : Application d'égalisation (EQ) avec EQP KT et Fabfilter Pro Suite pour sculpter le timbre de chaque instrument, compression avec 1176 KT et 4-710D Twin-Finity pour contrôler la dynamique, et effets via UAD (réverbération, délai, modulation) pour enrichir et spatialiser les sons.</li>
                <li><strong>Gestion de la dynamique</strong> : Contrôle de la dynamique de chaque piste et du morceau dans son ensemble avec SSL FUSION pour créer de la cohérence et de la clarté dans le mix.</li>
                <li><strong>Création d'une image stéréo équilibrée</strong> : Construction d'un mix cohérent où chaque élément a sa place, où les fréquences se complètent sans se chevaucher, et où l'ensemble forme un tout harmonieux et professionnel.</li>
              </ul>
              <p>Le mixage transforme vos enregistrements bruts en un morceau cohérent et professionnel, prêt pour le mastering.</p>
            </div>
          </div>
        )}
        
        {showEnregistrementPopup && (
          <div className="service-popup-overlay" onClick={() => setShowEnregistrementPopup(false)}>
            <div className="service-popup" onClick={(e) => e.stopPropagation()}>
              <button className="service-popup-close" onClick={() => setShowEnregistrementPopup(false)}>×</button>
              <h3>ENREGISTREMENT</h3>
              <p>L'enregistrement consiste à capturer voix, instruments et sources sonores dans un environnement acoustique optimal. Notre studio est équipé de matériel professionnel pour garantir des prises de qualité, avec une expertise technique qui vous guide à chaque étape.</p>
              <p>Nous proposons :</p>
              <ul>
                <li><strong>Enregistrement de voix</strong> : Captation de chant, narration, doublage ou voix parlée avec microphones professionnels (WA 14, WA 19, SM 57, SM 58, Beta 58, Beta 52, DUO A1). Techniques de placement adaptées à chaque voix avec préamplification via 4-710D Twin-Finity et interface APOLLO X16.</li>
                <li><strong>Enregistrement d'instruments</strong> : Prise de son d'instruments acoustiques (guitares, basses, batterie) et électroniques (synthétiseurs, boîtes à rythmes) avec choix de microphones et techniques de placement adaptés à chaque source sonore pour capturer leur caractère unique.</li>
                <li><strong>Matériel professionnel</strong> : Mise à disposition de microphones de qualité studio (dynamiques, à condensateur, à ruban), préamplis 4-710D Twin-Finity, interface audio APOLLO X16 et équipements de monitoring pour garantir des enregistrements de niveau professionnel.</li>
                <li><strong>Expertise technique</strong> : Accompagnement dans le choix des microphones, le placement optimal, les réglages de gain via APOLLO X16 et les techniques d'enregistrement. Conseils pour éviter les problèmes courants (saturation, bruit, résonances) et obtenir des prises propres dès la source.</li>
                <li><strong>Environnement acoustique traité</strong> : Studio avec traitement acoustique adapté pour minimiser les réflexions indésirables, contrôler la réverbération naturelle et créer un environnement d'enregistrement optimal selon le type de source.</li>
              </ul>
              <p>Nous vous accompagnons pour capturer votre performance dans les meilleures conditions et obtenir des enregistrements de qualité professionnelle.</p>
            </div>
          </div>
        )}
        
        {showProductionPopup && (
          <div className="service-popup-overlay" onClick={() => setShowProductionPopup(false)}>
            <div className="service-popup" onClick={(e) => e.stopPropagation()}>
              <button className="service-popup-close" onClick={() => setShowProductionPopup(false)}>×</button>
              <h3>PRODUCTION</h3>
              <p>La production musicale est un accompagnement complet de votre projet, de l'idée initiale à la réalisation finale. Nous vous aidons à donner vie à votre vision artistique en vous offrant une expertise technique et créative à chaque étape de votre parcours musical.</p>
              <p>Notre processus inclut :</p>
              <ul>
                <li><strong>Création et composition</strong> : Développement de morceaux originaux depuis une simple mélodie ou un concept, avec création de structures, développement d'idées musicales et composition de parties instrumentales complètes. Utilisation de synthétiseurs analogiques (DX7, JUNO 106, SH 101, RE 303, MONO/POLY), modulaire, Polyend TRACKER, Elektron Modèle Cycles et PUSH 2 avec Ableton Live 12 SUITE pour créer des textures sonores uniques.</li>
                <li><strong>Arrangement et orchestration</strong> : Organisation des éléments musicaux, choix des instruments (guitares, basses, batterie), création de textures sonores avec ARTURIA V COLLECTION et construction de l'architecture du morceau pour maximiser l'impact et la cohérence musicale.</li>
                <li><strong>Conseil artistique et direction musicale</strong> : Accompagnement dans vos choix créatifs, aide à définir votre identité sonore, feedback constructif et guidance pour faire évoluer votre projet dans la bonne direction.</li>
                <li><strong>Production technique</strong> : Prise en charge de l'aspect technique de la production avec Ableton Live 12 SUITE, programmation de séquences, création de boucles, design sonore avec UAD et Fabfilter Pro Suite, et intégration de samples pour enrichir votre composition. Traitement via APOLLO X16 et chaîne analogique (SSL FUSION, 4-710D Twin-Finity, 1176 KT, EQP KT).</li>
                <li><strong>Accompagnement personnalisé</strong> : Adaptation de notre approche à votre style, vos objectifs et votre niveau, que vous soyez débutant ou expérimenté, pour un résultat qui correspond à vos attentes.</li>
              </ul>
              <p>Que vous ayez une idée vague ou un projet déjà avancé, nous vous accompagnons pour transformer votre vision en réalité sonore professionnelle.</p>
            </div>
          </div>
        )}
      </section>

          <section id="contact" className="contact-section">
            <div className="container">
              <div className="page-section-title">
                <h2 className="contact-title">CONTACT</h2>
              </div>
          <div className="contact-content">
            <div className="contact-zone-1">
              <h3>NOS RESEAUX</h3>
              <div className="social-icons">
                <a href="https://www.instagram.com/o.t.r._studio/" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <img src="/assets/images/instaicoe.png" alt="Instagram" />
                </a>
                <a href="https://www.superprof.fr/ingenieur-compositeur-waveform-studio-conseils-developpement-projets-personnels-environnement-studio.html" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <img src="/assets/images/superproficoe.png" alt="Superprof" />
                </a>
                <a href="https://www.studiomap.fr/studios/studio-denregistrement-creatif-a-bordeaux-33000" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <img src="/assets/images/studiomap icone.png" alt="Studio Map" />
                </a>
              </div>
            </div>
            
            <div className="contact-zone-2">
              <div className="about-section">
                <h3>A PROPOS</h3>
                <p>Au studio, tu pourras retrouver <strong>MAX et ELIOTT</strong>, deux ingés son et compositeurs de musique qui sauront te conseiller et accompagner tes sessions avec <strong>humour et bonne humeur</strong>. Passionnés de synthés vintages et de musique électronique, ils aiment venir titiller le grain des projets qu'on leur confie. Ils maîtrisent les synthés analogiques et les chaînes vintage pour enrichir tes projets. Ils privilégient un son authentique et chaleureux, en tirant le meilleur parti de ce matériel classique.</p>
                <button className="studio-btn" onClick={() => setShowPhotoGallery(true)}>PHOTOS DU STUDIO {'>'}</button>
              </div>
            </div>
            
                <div className="contact-zone-3">
                  <div className="meet-section">
                    <h3>RENCONTREZ NOUS...</h3>
                    <span className="separator-bar separator-bar-meet">
                      <img 
                        src="/assets/images/separateur icone.png" 
                        alt=""
                        role="presentation"
                        className="meet-separator"
                      />
                    </span>
                    <p>Rencontrons-nous pour discuter de votre <strong>projet musical</strong>. Venez découvrir notre <strong>studio</strong>, explorer nos <strong>synthés analogiques</strong> et nos <strong>équipements</strong>. C'est l'occasion parfaite d'échanger sur vos idées, <strong>tester les sonorités</strong> et définir ensemble votre <strong>direction artistique</strong>.</p>
                    <button className="contact-btn" onClick={() => setShowContactPopup(true)}>CONTACT {'>'}</button>
                  </div>
                </div>
            
                <div className="contact-zone-4">
                  <div className="map-container">
                    <iframe 
                      src="https://www.google.com/maps?q=21+Rue+Gratiolet,+33000+Bordeaux&output=embed"
                      width="100%" 
                      height="100%" 
                      style={{border: 0}} 
                      allowFullScreen="" 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="OTR STUDIO - 21 Rue Gratiolet, 33000 Bordeaux">
                    </iframe>
                  </div>
                </div>
          </div>
        </div>
      </section>

      {/* Popup vidéo Show Reel */}
      {showVideoPopup && (
        <div className="video-popup-overlay" onClick={() => setShowVideoPopup(false)}>
          <div className="video-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-popup-close" onClick={() => setShowVideoPopup(false)}>
              <span className="close-icon">✕</span>
            </button>
            <div className="video-container">
              <iframe
                className="showreel-video"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&mute=1"
                title="OTR STUDIO - Show Reel"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Popup Sound Catalog */}
      {showSoundCatalogPopup && (
        <div className="sound-catalog-popup-overlay" onClick={() => {
          // Si un track est en attente, le lancer au premier clic (interaction utilisateur)
          if (pendingTrackFromUrl && audioFromUrlRef.current) {
            const audio = audioFromUrlRef.current;
            audio.play().then(() => {
              console.log('Lecture démarrée après interaction utilisateur (overlay)');
              setPendingTrackFromUrl(null);
              audioFromUrlRef.current = null;
              isLoadingTrackRef.current = false;
            }).catch(err => {
              console.error('Erreur lors de la lecture après interaction:', err);
            });
          }
          setShowSoundCatalogPopup(false);
        }}>
          <div className="sound-catalog-popup-content" onClick={(e) => {
            e.stopPropagation();
            // Si un track est en attente, le lancer au premier clic (interaction utilisateur)
            if (pendingTrackFromUrl && audioFromUrlRef.current) {
              const audio = audioFromUrlRef.current;
              audio.play().then(() => {
                console.log('Lecture démarrée après interaction utilisateur (content)');
                setPendingTrackFromUrl(null);
                audioFromUrlRef.current = null;
                isLoadingTrackRef.current = false;
              }).catch(err => {
                console.error('Erreur lors de la lecture après interaction:', err);
              });
            }
          }}>
            <button className="sound-catalog-popup-close" onClick={() => setShowSoundCatalogPopup(false)}>
              <span className="close-icon">✕</span>
            </button>
            <div className="sound-catalog-body">
            {/* Message si autoplay bloqué */}
            {pendingTrackFromUrl && (
              <div className="autoplay-blocked-message" onClick={() => {
                if (audioFromUrlRef.current) {
                  const audio = audioFromUrlRef.current;
                  audio.play().then(() => {
                    console.log('Lecture démarrée après interaction utilisateur');
                    setPendingTrackFromUrl(null);
                    audioFromUrlRef.current = null;
                    isLoadingTrackRef.current = false;
                  }).catch(err => {
                    console.error('Erreur lors de la lecture après interaction:', err);
                  });
                }
              }}>
                <div className="autoplay-blocked-content">
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{width: '24px', height: '24px'}}>
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  <span>Cliquez pour lancer la lecture de "{pendingTrackFromUrl.name}"</span>
                </div>
              </div>
            )}
            <div className="sound-catalog-header">
              <h2 className="sound-catalog-header-title">
                SOUND CATALOG
                <span
                  className="sound-catalog-header-info-icon"
                  title="Musiques originales pour vos projets de synchro et publicité. Créations prêtes à l'emploi, pensées pour les professionnels de la musique à l'image. Écoutez, trouvez votre son, contactez-nous pour les licences."
                  aria-label="En savoir plus sur le Sound Catalog"
                >
                  ?
                </span>
              </h2>
            </div>
              
            <audio ref={audioRef} preload="none" />
            {/* Wrapper pour que la barre soit en overlay : le backdrop du blur = la playlist qui défile */}
            <div className="sound-catalog-scroll-wrapper">
              <div className="sound-catalog-scroll-area">
                <div className="sound-catalog-playlist">
              {filteredAudioFiles.length === 0 ? (
                <div className="no-results">
                  <p>Aucun morceau trouvé</p>
                  </div>
              ) : (
                filteredAudioFiles.map((track) => {
                const isPlaying = currentlyPlaying === track.id;
                return (
                  <div 
                    key={track.id} 
                    className={`playlist-item ${isPlaying ? 'playing' : ''}`}
                    onClick={() => {
                      // Toujours arrêter toutes les lectures avant de lancer une nouvelle
                      // Cela garantit que l'audio du partage est bien arrêté
                      stopAllAudio();
                      
                      if (isPlaying) {
                        // Si c'était déjà en cours, on s'arrête là
                        return;
                      }
                      
                      // Attendre un peu pour s'assurer que tout est bien arrêté
                      setTimeout(() => {
                        // Réinitialiser les états
                        setCurrentTime(0);
                        setDuration(0);
                        
                        // Charger le nouveau fichier
                        const audio = new Audio(getTrackUrl(track));
                        audio.volume = volume;
                        audioRef.current = audio;
                        setCurrentlyPlaying(track.id);
                        
                        // Stocker l'instance audio
                        audioInstancesRef.current.set(track.id, audio);
                        
                        // Gérer la fin de lecture
                        const handleEnded = () => {
                          if (audioInstancesRef.current.has(track.id)) {
                            audioInstancesRef.current.delete(track.id);
                          }
                          if (currentlyPlaying === track.id) {
                            setCurrentlyPlaying(null);
                            setCurrentTime(0);
                            setDuration(0);
                            if (audioRef.current === audio) {
                              audioRef.current = null;
                            }
                          }
                        };
                        
                        // Gérer le chargement des métadonnées pour obtenir la durée
                        const handleLoadedMetadata = () => {
                          if (currentlyPlaying === track.id && audio.duration) {
                            setDuration(audio.duration);
                          }
                        };
                        
                        audio.addEventListener('ended', handleEnded);
                        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
                        
                        // Jouer le MP3
                        audio.play().catch(err => {
                          console.error('Erreur de lecture:', err);
                          audioInstancesRef.current.delete(track.id);
                          if (currentlyPlaying === track.id) {
                            setCurrentlyPlaying(null);
                          }
                          if (audioRef.current === audio) {
                            audioRef.current = null;
                          }
                        });
                      }, 100); // Petit délai pour s'assurer que stopAllAudio a fini
                    }}
                  >
                <div className="track-info">
                  <div className="track-details">
                    <h4>{track.name}</h4>
                  </div>
                </div>
                </div>
                );
                })
              )}
                </div>
              </div>
              {/* Barre de recherche en overlay : verre dépoli (blur) sur la playlist qui défile */}
              <div className="sound-catalog-filters">
                <div className="search-container">
                  <div className="search-input-glass">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Rechercher un morceau..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <button 
                    className="toggle-categories-btn"
                    onClick={() => setShowCategories(!showCategories)}
                    aria-label="Afficher/masquer les catégories"
                  >
                    {showCategories ? '▼' : '▶'} Catégories
                  </button>
                </div>
                <div className={`category-filters ${showCategories ? 'visible' : ''}`}>
                  <button
                    className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory('all');
                      setShowCategories(false);
                    }}
                  >
                    TOUS
                  </button>
                  {categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <button
                        key={category}
                        className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedCategory(category);
                          setShowCategories(false);
                        }}
                      >
                        {category}
                      </button>
                    ))
                  ) : null}
                </div>
              </div>
            </div>
              
            {/* Lecteur audio en bas - disposition type référence (titre gauche, actions droite, barre au centre, contrôles en bas) */}
            <div className="sound-catalog-footer">
            <div className="audio-player-bottom catalog-player-wrapper">
              {currentlyPlaying ? (
                <>
                <div className="catalog-player-layout">
                  {/* 1. Ligne du haut : titre + sous-titre à gauche, icônes (partage + volume) à droite */}
                  <div className="catalog-player-row catalog-player-header">
                    <div className="catalog-player-track-info">
                      {(() => {
                        const currentTrack = audioFiles.find(t => t.id === currentlyPlaying);
                        return currentTrack ? (
                          <div className="audio-player-track-name">{currentTrack.name}</div>
                        ) : null;
                      })()}
                    </div>
                    <div className="catalog-player-actions">
                      <button
                        type="button"
                        className="catalog-player-action-btn share-button"
                        onClick={() => copyShareLink(currentlyPlaying)}
                        title="Partager ce morceau"
                        aria-label="Partager ce morceau"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                        </svg>
                      </button>
                      <div className="audio-player-volume-container catalog-player-volume">
                        <button 
                          type="button"
                          className="volume-icon-button"
                          onClick={() => window.innerWidth <= 768 && setShowVolumeSlider(!showVolumeSlider)}
                          onMouseEnter={() => window.innerWidth > 768 && setShowVolumeSlider(true)}
                          onMouseLeave={() => window.innerWidth > 768 && (volumeSliderTimeoutRef.current = setTimeout(() => setShowVolumeSlider(false), 1500))}
                          aria-label={volume === 0 ? 'Activer le son' : 'Volume'}
                        >
                          <svg className="volume-icon" viewBox="0 0 24 24" fill="currentColor">
                            {volume === 0 ? (
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                            ) : volume < 0.5 ? (
                              <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
                            ) : (
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                            )}
                          </svg>
                        </button>
                        <div className={`audio-player-volume-slider-wrapper desktop-volume ${showVolumeSlider ? 'visible' : ''}`}>
                          <input type="range" min="0" max="1" step="0.01" value={volume} className="audio-player-volume desktop-volume-slider"
                            onChange={(e) => setVolume(parseFloat(e.target.value))} />
                        </div>
                        <div className={`audio-player-volume-slider-wrapper mobile-volume ${showVolumeSlider ? 'visible' : ''}`}>
                          <input type="range" min="0" max="1" step="0.01" value={volume} className="audio-player-volume mobile-volume-slider" onChange={(e) => setVolume(parseFloat(e.target.value))} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. Ligne barre de progression : temps gauche | barre | temps droite */}
                  <div className="catalog-player-row catalog-player-progress-row">
                    <span className="catalog-player-time-left">{formatTime(currentTime || 0)}</span>
                    <div className="audio-player-progress-container" role="region" aria-label="Barre de lecture">
                      <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        value={currentTime || 0}
                        step="0.1"
                        className="audio-player-progress"
                        aria-label="Position dans la piste"
                        aria-valuemin={0}
                        aria-valuemax={duration || 0}
                        aria-valuenow={currentTime || 0}
                        aria-valuetext={duration ? `${formatTime(currentTime || 0)} sur ${formatTime(duration)}` : undefined}
                        onChange={(e) => {
                          const newTime = parseFloat(e.target.value);
                          setCurrentTime(newTime);
                          if (audioRef.current) audioRef.current.currentTime = newTime;
                        }}
                        onMouseDown={() => setIsSeeking(true)}
                        onMouseUp={() => setIsSeeking(false)}
                        onTouchStart={() => setIsSeeking(true)}
                        onTouchEnd={() => setIsSeeking(false)}
                      />
                    </div>
                    <span className="catalog-player-time-right">-{duration ? formatTime(Math.max(0, duration - (currentTime || 0))) : '0:00'}</span>
                  </div>

                  {/* 3. Ligne contrôles : précédent | play/pause | suivant (centrés, gros boutons blancs) */}
                  <div className="catalog-player-row catalog-player-controls">
                    <button 
                      className="audio-player-previous"
                      onClick={() => {
                        if (currentlyPlaying && filteredAudioFiles.length > 0) {
                          const currentIndex = filteredAudioFiles.findIndex(t => t.id === currentlyPlaying);
                          if (currentIndex !== -1 && currentIndex > 0) {
                            const previousTrack = filteredAudioFiles[currentIndex - 1];
                            const previousTrackId = previousTrack.id;
                            
                            // Arrêter la lecture actuelle
                            stopAllAudio();
                            
                            // Réinitialiser les états
                            setCurrentTime(0);
                            setDuration(0);
                            
                            // Charger le nouveau fichier
                            const audio = new Audio(getTrackUrl(previousTrack));
                            audio.volume = volume;
                            audioRef.current = audio;
                            setCurrentlyPlaying(previousTrackId);
                            audioInstancesRef.current.set(previousTrackId, audio);
                            
                            const handleEnded = () => {
                              if (audioInstancesRef.current.has(previousTrackId)) {
                                audioInstancesRef.current.delete(previousTrackId);
                              }
                              setCurrentlyPlaying(null);
                              setCurrentTime(0);
                              setDuration(0);
                              if (audioRef.current === audio) {
                                audioRef.current = null;
                              }
                            };
                            
                            const handleLoadedMetadata = () => {
                              if (audio.duration) {
                                setDuration(audio.duration);
                              }
                            };
                            
                            audio.addEventListener('ended', handleEnded);
                            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
                            audio.play().catch(err => {
                              console.error('Erreur de lecture:', err);
                              audioInstancesRef.current.delete(previousTrackId);
                              setCurrentlyPlaying(null);
                              if (audioRef.current === audio) {
                                audioRef.current = null;
                              }
                            });
                          }
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                      </svg>
                    </button>
                    
                    <button 
                      type="button"
                      className="audio-player-play-pause"
                      aria-label={audioRef.current && !audioRef.current.paused ? 'Mettre en pause' : 'Lire'}
                      onClick={() => {
                        if (audioRef.current) {
                          if (audioRef.current.paused) {
                            audioRef.current.play();
                          } else {
                            audioRef.current.pause();
                          }
                        }
                      }}
                    >
                      {audioRef.current && !audioRef.current.paused ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </button>
                    
                    {/* Bouton suivant - visible sur mobile */}
                    <button 
                      className="audio-player-next"
                      onClick={() => {
                        if (currentlyPlaying && filteredAudioFiles.length > 0) {
                          const currentIndex = filteredAudioFiles.findIndex(t => t.id === currentlyPlaying);
                          if (currentIndex !== -1 && currentIndex < filteredAudioFiles.length - 1) {
                            const nextTrack = filteredAudioFiles[currentIndex + 1];
                            const nextTrackId = nextTrack.id;
                            
                            // Arrêter la lecture actuelle
                            stopAllAudio();
                            
                            // Réinitialiser les états
                            setCurrentTime(0);
                            setDuration(0);
                            
                            // Charger le nouveau fichier
                            const audio = new Audio(getTrackUrl(nextTrack));
                            audio.volume = volume;
                            audioRef.current = audio;
                            setCurrentlyPlaying(nextTrackId);
                            audioInstancesRef.current.set(nextTrackId, audio);
                            
                            const handleEnded = () => {
                              if (audioInstancesRef.current.has(nextTrackId)) {
                                audioInstancesRef.current.delete(nextTrackId);
                              }
                              setCurrentlyPlaying(null);
                              setCurrentTime(0);
                              setDuration(0);
                              if (audioRef.current === audio) {
                                audioRef.current = null;
                              }
                            };
                            
                            const handleLoadedMetadata = () => {
                              if (audio.duration) {
                                setDuration(audio.duration);
                              }
                            };
                            
                            audio.addEventListener('ended', handleEnded);
                            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
                            audio.play().catch(err => {
                              console.error('Erreur de lecture:', err);
                              audioInstancesRef.current.delete(nextTrackId);
                              setCurrentlyPlaying(null);
                              if (audioRef.current === audio) {
                                audioRef.current = null;
                              }
                            });
                          }
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                      </svg>
                    </button>
                    
                    {/* Popup "lien copié" - rendu une seule fois (dans le player) */}
                    {shareLinkCopied && (
                      <div className="share-copied-popup">
                        <div className="share-copied-popup-content">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="share-copied-icon">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                          <span className="share-copied-text">lien copié</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </>
              ) : (
                <div className="audio-player-no-track">
                  <p>Aucune lecture en cours</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Popup Équipement/Matériel */}
      {showEquipmentPopup && (
        <div className="equipment-popup-overlay" onClick={() => { setShowEquipmentPopup(false); setExpandedEquipmentCategory(null); }}>
          <div className="equipment-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="equipment-popup-close" onClick={() => { setShowEquipmentPopup(false); setExpandedEquipmentCategory(null); }}>
              <span className="close-icon">✕</span>
            </button>
            <div className="equipment-header">
              <h2>MATERIEL DISPONIBLE</h2>
            </div>
            <div 
              className="equipment-categories"
              onClick={(e) => {
                // Fermer la fiche si on clique en dehors des items sur mobile
                if (isMobile && e.target.classList.contains('equipment-categories')) {
                  setClickedEquipment(null);
                  setHoveredEquipment(null);
                }
              }}
            >
              {/* Hardware */}
              <div className="equipment-category" data-category="hardware">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'hardware' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'hardware' ? null : 'hardware'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'hardware' ? null : 'hardware'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'hardware' : undefined}
                >
                  <span className="category-icon">🎚️</span>
                  <h3>HARDWARE</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'hardware') && (
                <div className="equipment-list">
                  {['APOLLO X16', 'SSL FUSION', '4-710D Twin-Finity', '2X 1176 KT', '2X EQP KT', 'Revox A77', 'WZ4 16:2', 'Boss BX8', 'PUSH 2'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Software */}
              <div className="equipment-category" data-category="software">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'software' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'software' ? null : 'software'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'software' ? null : 'software'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'software' : undefined}
                >
                  <span className="category-icon">💻</span>
                  <h3>SOFTWARE</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'software') && (
                <div className="equipment-list">
                  {['Ableton Live 12 SUITE', 'UAD & UAD SPARK', 'ARTURIA V COLLECTION', 'Fabfilter Pro Suite'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Micros */}
              <div className="equipment-category" data-category="micros">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'micros' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'micros' ? null : 'micros'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'micros' ? null : 'micros'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'micros' : undefined}
                >
                  <span className="category-icon">🎤</span>
                  <h3>MICROS</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'micros') && (
                <div className="equipment-list">
                  {['WA 14', 'WA 19', 'X 1 S', 'SM 57', 'SM 58', 'Beta 58', 'Beta 52', 'DUO A1'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Synths */}
              <div className="equipment-category" data-category="synth">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'synth' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'synth' ? null : 'synth'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'synth' ? null : 'synth'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'synth' : undefined}
                >
                  <span className="category-icon">🎹</span>
                  <h3>SYNTH</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'synth') && (
                <div className="equipment-list">
                  {['DX7', 'JUNO 106', 'SH 101', 'RE 303', 'MONO/POLY', 'Polyend TRACKER', 'Elektron Modèle Cycles', 'MODULAIRE DIVERS'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Guitares/Basses */}
              <div className="equipment-category" data-category="guitares">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'guitares' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'guitares' ? null : 'guitares'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'guitares' ? null : 'guitares'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'guitares' : undefined}
                >
                  <span className="category-icon">🎸</span>
                  <h3>GUITARES / BASSES</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'guitares') && (
                <div className="equipment-list">
                  {['Rickenbacker 330', 'PRS', 'Fender Jazzmaster', 'Fender Stratocaster', 'Hofner Violin Bass', 'Squier Mustang Bass'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Batterie */}
              <div className="equipment-category" data-category="drums">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'drums' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'drums' ? null : 'drums'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'drums' ? null : 'drums'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'drums' : undefined}
                >
                  <span className="category-icon">🥁</span>
                  <h3>DRUMS</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'drums') && (
                <div className="equipment-list">
                  {['Sonor AQ1', 'Premier Artist Heritage 16', 'Divers cymbales'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Pedales */}
              <div className="equipment-category" data-category="pedales">
                <div
                  className={`category-title ${isMobile ? 'category-title-tappable' : ''} ${expandedEquipmentCategory === 'pedales' ? 'category-title-expanded' : ''}`}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={() => isMobile && setExpandedEquipmentCategory((v) => (v === 'pedales' ? null : 'pedales'))}
                  onKeyDown={(e) => isMobile && (e.key === 'Enter' || e.key === ' ') && setExpandedEquipmentCategory((v) => (v === 'pedales' ? null : 'pedales'))}
                  aria-expanded={isMobile ? expandedEquipmentCategory === 'pedales' : undefined}
                >
                  <span className="category-icon">🎛️</span>
                  <h3>PEDALES</h3>
                  {isMobile && <span className="category-chevron" aria-hidden="true">▼</span>}
                </div>
                {(!isMobile || expandedEquipmentCategory === 'pedales') && (
                <div className="equipment-list">
                  {['Whammy 5', 'H9 MAX', 'BD-2W', 'UAFX LA-2A', 'MF-101', 'RAT', 'WARM BENDER'].map((item) => (
                    <div 
                      key={item}
                      className="equipment-item"
                      onClick={(e) => {
                        if (isMobile && equipmentData[item]) {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          
                          // Toggle : si c'est le même item, fermer, sinon ouvrir le nouveau
                          if (clickedEquipment === item) {
                            setClickedEquipment(null);
                            setHoveredEquipment(null);
                          } else {
                            setClickedEquipment(item);
                            setHoveredEquipment(item);
                          }
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && equipmentData[item]) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const position = calculateTooltipPosition(rect);
                          setEquipmentTooltipPosition(position);
                          setHoveredEquipment(item);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setHoveredEquipment(null);
                        }
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>
            
            {/* Fiche technique au survol/clic */}
            {hoveredEquipment && equipmentData[hoveredEquipment] && (
              <div 
                className="equipment-tooltip"
                style={{
                  left: `${equipmentTooltipPosition.x}px`,
                  top: `${equipmentTooltipPosition.y}px`
                }}
                onMouseEnter={() => {
                  if (!isMobile) {
                    // Garder la tooltip visible sur desktop
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile) {
                    setHoveredEquipment(null);
                  }
                }}
                onClick={(e) => {
                  if (isMobile) {
                    // Fermer au clic sur la tooltip elle-même sur mobile
                    e.stopPropagation();
                    setClickedEquipment(null);
                    setHoveredEquipment(null);
                  }
                }}
              >
                <div className="equipment-tooltip-image">
                  <img 
                    src={equipmentData[hoveredEquipment].image} 
                    alt={equipmentData[hoveredEquipment].name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="equipment-tooltip-content">
                  <h4>{equipmentData[hoveredEquipment].name}</h4>
                  <p>{equipmentData[hoveredEquipment].description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popup Galerie Photo */}
      {showPhotoGallery && (
        <div className="photo-gallery-overlay" onClick={() => setShowPhotoGallery(false)}>
          <div className="photo-gallery-content" onClick={(e) => e.stopPropagation()}>
            <button className="photo-gallery-close" onClick={() => setShowPhotoGallery(false)}>
              <span className="close-icon">✕</span>
            </button>
            <div className="photo-gallery-header">
              <h2>PHOTOS DU STUDIO</h2>
              <p>Découvrez l'espace de création OTR STUDIO</p>
            </div>
            <div className="photo-gallery-grid">
              {[
                { src: '/assets/images/studio-photos/069A0011.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0020.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0026.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0024.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0034.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0038.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0042.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0046.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0050.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0048.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0016.JPG', title: 'Studio' },
                { src: '/assets/images/studio-photos/069A0040.JPG', title: 'Studio' }
              ].map((photo, index) => (
                <div 
                  key={index} 
                  className={`gallery-item ${index === 0 ? 'gallery-item-first' : ''}`}
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <div className="photo-placeholder">
                    <img src={photo.src} alt={photo.title} />
                  </div>
                </div>
              ))}
            </div>

            {/* Modal d'agrandissement */}
            {selectedPhoto && (
              <div className="photo-zoom-overlay" onClick={() => setSelectedPhoto(null)}>
                <button className="photo-zoom-close" onClick={() => setSelectedPhoto(null)}>
                  <span className="close-icon">✕</span>
                </button>
                <div className="photo-zoom-content" onClick={(e) => e.stopPropagation()}>
                  <div className={`photo-zoom-image ${selectedPhoto.src.includes('069A0011') ? 'photo-zoom-image-brighten' : ''}`}>
                    <img src={selectedPhoto.src} alt={selectedPhoto.title} />
                    <p>{selectedPhoto.title}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popup Contact */}
      {showContactPopup && (
        <div className="contact-popup-overlay" onClick={() => setShowContactPopup(false)}>
          <div className="contact-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="contact-popup-close" onClick={() => setShowContactPopup(false)}>
              <span className="close-icon">✕</span>
            </button>
            <div className="contact-popup-header">
              <h2>CONTACTEZ-NOUS</h2>
              <p>Racontez-nous votre projet</p>
            </div>
            <form 
              className="contact-form"
              id="contact-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const name = formData.get('name');
                const email = formData.get('email');
                const message = formData.get('message');
                
                // Validation stricte de l'email
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!email || !emailRegex.test(email)) {
                  alert('Veuillez entrer une adresse email valide.');
                  return;
                }
                
                // Vérifier que tous les champs sont remplis
                if (!name || !message) {
                  alert('Veuillez remplir tous les champs obligatoires.');
                  return;
                }
                
                // Utiliser FormSubmit avec configuration optimisée
                try {
                  const response = await fetch('https://formsubmit.co/ajax/contact@otrstudio.fr', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                      name: name,
                      email: email,
                      message: message,
                      _subject: `Contact depuis OTR STUDIO - ${name}`,
                      _captcha: false,
                      _template: 'box',
                      _autoresponse: `Bonjour ${name},\n\nMerci pour votre message ! Nous avons bien reçu votre demande et vous répondrons dans les plus brefs délais.\n\nÀ bientôt,\nL'équipe OTR STUDIO`
                    })
                  });
                  
                  const result = await response.json();
                  
                  if (result.success) {
                    alert('✅ Message envoyé avec succès ! L\'équipe OTR STUDIO vous répondra rapidement.\n\n💡 Important : Vérifiez votre dossier SPAM/COURRIER INDÉSIRABLE si vous ne recevez pas l\'email dans quelques minutes.');
                    e.target.reset();
                    setShowContactPopup(false);
                  } else {
                    throw new Error(result.message || 'Erreur lors de l\'envoi');
                  }
                } catch (error) {
                  console.error('Erreur FormSubmit:', error);
                  
                  // Fallback : méthode HTML classique si l'API échoue
                  const tempForm = document.createElement('form');
                  tempForm.action = 'https://formsubmit.co/contact@otrstudio.fr';
                  tempForm.method = 'POST';
                  tempForm.style.display = 'none';
                  
                  const nameInput = document.createElement('input');
                  nameInput.type = 'hidden';
                  nameInput.name = 'name';
                  nameInput.value = name;
                  tempForm.appendChild(nameInput);
                  
                  const emailInput = document.createElement('input');
                  emailInput.type = 'hidden';
                  emailInput.name = 'email';
                  emailInput.value = email;
                  tempForm.appendChild(emailInput);
                  
                  const messageInput = document.createElement('input');
                  messageInput.type = 'hidden';
                  messageInput.name = 'message';
                  messageInput.value = message;
                  tempForm.appendChild(messageInput);
                  
                  const subjectInput = document.createElement('input');
                  subjectInput.type = 'hidden';
                  subjectInput.name = '_subject';
                  subjectInput.value = `Contact depuis OTR STUDIO - ${name}`;
                  tempForm.appendChild(subjectInput);
                  
                  const captchaInput = document.createElement('input');
                  captchaInput.type = 'hidden';
                  captchaInput.name = '_captcha';
                  captchaInput.value = 'false';
                  tempForm.appendChild(captchaInput);
                  
                  const nextInput = document.createElement('input');
                  nextInput.type = 'hidden';
                  nextInput.name = '_next';
                  nextInput.value = window.location.href + '?success=true';
                  tempForm.appendChild(nextInput);
                  
                  document.body.appendChild(tempForm);
                  tempForm.submit();
                  
                  alert('✅ Message envoyé avec succès ! L\'équipe OTR STUDIO vous répondra rapidement.\n\n💡 Important : Vérifiez votre dossier SPAM/COURRIER INDÉSIRABLE si vous ne recevez pas l\'email dans quelques minutes.');
                  e.target.reset();
                  setShowContactPopup(false);
                  
                  setTimeout(() => {
                    if (document.body.contains(tempForm)) {
                      document.body.removeChild(tempForm);
                    }
                  }, 2000);
                }
              }}
            >
              <div className="form-group">
                <label htmlFor="name">Nom *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  required 
                  placeholder="Votre nom"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  required 
                  placeholder="votre.email@exemple.com"
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  title="Veuillez entrer une adresse email valide"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="8" 
                  required 
                  placeholder="Décrivez votre projet, vos besoins, vos questions..."
                ></textarea>
              </div>
              <input type="hidden" name="to_email" value="contact@otrstudio.fr" />
              <button type="submit" className="contact-submit-btn">
                ENVOYER
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;

